import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, AlertCircle, CheckCircle2 } from 'lucide-react';
import { api } from '@/api/client';

interface HealthStatus {
  app: string;
  database: string;
  kb: string;
  php_version?: string;
  timestamp?: string;
}

export function Dashboard() {
  const [health, setHealth] = useState<HealthStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.health()
      .then(setHealth)
      .catch((e) => setError(e.message));
  }, []);

  return (
    <div className="max-w-5xl">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-8">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Dashboard</h2>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Overview of your knowledge base</p>
        </div>
        <Link to="/workflows/new" className="btn-primary">
          <Plus size={16} className="mr-2" />
          New Workflow
        </Link>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">System Status</h3>
        {error && (
          <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg text-red-800 dark:text-red-400">
            <AlertCircle size={18} className="mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-medium">Cannot reach API</div>
              <div className="text-sm mt-1">{error}</div>
              <div className="text-xs mt-2">
                Make sure the PHP backend is running at{' '}
                <code className="px-1 py-0.5 bg-red-100 dark:bg-red-800/50 rounded">
                  http://localhost/promed-support-api
                </code>
              </div>
            </div>
          </div>
        )}
        {health && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatusCard label="Application" value={health.app} />
            <StatusCard label="Database" value={health.database} />
            <StatusCard label="Knowledge Base" value={health.kb} />
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Link
          to="/workflows"
          className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 hover:border-brand-500 dark:hover:border-brand-400 transition-colors"
        >
          <h3 className="font-semibold text-gray-900 dark:text-white">Manage Workflows</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Create, edit, and organize support workflows
          </p>
        </Link>
        <Link
          to="/test"
          className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 hover:border-brand-500 dark:hover:border-brand-400 transition-colors"
        >
          <h3 className="font-semibold text-gray-900 dark:text-white">Test the Agent</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Try questions against your draft KB before publishing
          </p>
        </Link>
      </div>
    </div>
  );
}

function StatusCard({ label, value }: { label: string; value: string }) {
  const isOk = !value.toLowerCase().startsWith('error');
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 dark:bg-gray-700/30">
      <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">{label}</div>
      <div className="flex items-center gap-2">
        {isOk ? (
          <CheckCircle2 size={16} className="text-green-600 dark:text-green-500" />
        ) : (
          <AlertCircle size={16} className="text-red-600 dark:text-red-500" />
        )}
        <span className="text-sm text-gray-900 dark:text-white font-medium truncate">{value}</span>
      </div>
    </div>
  );
}
